import React, { useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ClipboardList,
  FileText,
  Loader2,
  Shield,
  Upload,
  User,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import claimService from "../../api/ClaimService";
import ClaimStepper, {
  REPRESENTATIVE_CLAIM_STEPS,
  SELF_CLAIM_STEPS,
} from "../../components/claims/ClaimStepper";
import { getClaimPolicySlug } from "../../utils/claimHelpers";

/* ─── animation variants ─────────────────────────────────────── */
const stepVariants = {
  enter:  { x: 40,  opacity: 0 },
  center: { x: 0,   opacity: 1 },
  exit:   { x: -40, opacity: 0 },
};

/* ─── shared input styles ────────────────────────────────────── */
const inputBase =
  "w-full h-12 px-3.5 text-sm bg-white rounded-xl border transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-300";
const inputNormal = `${inputBase} border-gray-200`;
const inputErr    = `${inputBase} border-red-400 bg-red-50 focus:ring-red-200`;
const labelCls    = "block text-xs font-semibold text-gray-700 mb-1.5";

const FieldError = ({ msg }) =>
  msg ? (
    <p className="flex gap-1 items-center mt-1 text-xs text-red-600">
      <AlertCircle size={11} className="shrink-0" /> {msg}
    </p>
  ) : null;

const shortenBase64Value = (value, keep = 64) => {
  if (typeof value !== "string") return value;
  const isDataUrl = value.startsWith("data:");
  const looksLongBase64 = value.length > keep * 2 && /^[A-Za-z0-9+/=\s]+$/.test(value);
  if (!isDataUrl && !looksLongBase64) return value;
  return `${value.slice(0, keep)}…[truncated ${value.length} chars]`;
};

const shortenPayloadForLog = (value) => {
  if (Array.isArray(value)) return value.map(shortenPayloadForLog);
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).map(([key, nested]) => [key, shortenPayloadForLog(nested)])
    );
  }
  return shortenBase64Value(value);
};

/* ─── sidebar stepper ────────────────────────────────────────── */
const SidebarStepper = ({ steps, currentIndex, policy, forSelf, representative, onStepClick }) => (
  <aside className="hidden lg:block w-64 shrink-0">
    <div className="fixed top-28 w-64 space-y-5">
      {/* step list */}
      <div>
        <p className="text-[10px] font-bold tracking-widest uppercase text-gray-400 mb-4">
          Claim progress
        </p>
        <ol className="space-y-1">
          {steps.map((label, idx) => {
            const done     = idx < currentIndex;
            const active   = idx === currentIndex;
            const canClick = done && !!onStepClick;
            return (
              <li key={label} className="flex gap-3 items-start">
                <div className="flex flex-col items-center mt-0.5">
                  <button
                    type="button"
                    disabled={!canClick}
                    onClick={() => canClick && onStepClick(idx)}
                    className={`grid place-items-center w-7 h-7 rounded-full text-xs font-bold shrink-0 transition-all ${
                      done    ? "bg-emerald-600 text-white shadow-sm shadow-emerald-200"
                      : active ? "bg-emerald-600 text-white ring-4 ring-emerald-100"
                      : "bg-gray-100 text-gray-400"
                    } ${canClick ? "cursor-pointer hover:ring-2 hover:ring-emerald-300 hover:scale-110" : "cursor-default"}`}
                    title={canClick ? `Go back to ${label}` : undefined}
                  >
                    {done ? <CheckCircle2 size={14} /> : idx + 1}
                  </button>
                  {idx < steps.length - 1 && (
                    <div className={`w-px flex-1 min-h-[28px] mt-1 ${done ? "bg-emerald-300" : "bg-gray-200"}`} />
                  )}
                </div>
                <div className="pb-5">
                  <p
                    onClick={() => canClick && onStepClick(idx)}
                    className={`text-sm leading-tight transition-colors ${
                      active  ? "font-bold text-gray-900"
                      : done  ? "font-semibold text-emerald-700 hover:text-emerald-800"
                      : "text-gray-400"
                    } ${canClick ? "cursor-pointer" : ""}`}
                  >{label}</p>
                  {active && (
                    <p className="text-[11px] text-emerald-600 font-medium mt-0.5">Current step</p>
                  )}
                  {done && canClick && (
                    <p className="text-[11px] text-gray-400 mt-0.5">Click to revisit</p>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      </div>

      {/* policy card */}
      {policy && (
        <div className="rounded-2xl border border-gray-100 bg-gray-50 p-4 space-y-3">
          <div className="flex gap-2 items-center">
            <Shield size={15} className="text-emerald-600 shrink-0" />
            <p className="text-xs font-bold text-gray-700">Policy</p>
          </div>
          {policy.provider?.name && (
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Cover</p>
              <p className="text-sm font-semibold text-gray-800 mt-0.5">{policy.provider.name}</p>
            </div>
          )}
        </div>
      )}

      {/* representative card */}
      {!forSelf && representative && (
        <div className="rounded-2xl border border-orange-100 bg-orange-50 p-4 space-y-2">
          <div className="flex gap-2 items-center">
            <User size={14} className="text-orange-500 shrink-0" />
            <p className="text-xs font-bold text-orange-800">Representative</p>
          </div>
          {representative.full_name && (
            <p className="text-sm font-semibold text-orange-900">{representative.full_name}</p>
          )}
          {representative.relationship && (
            <p className="text-xs text-orange-700">{representative.relationship}</p>
          )}
        </div>
      )}
    </div>
  </aside>
);

/* ─── reusable checkbox ──────────────────────────────────────── */
const Checkbox = ({ checked, onChange, children }) => (
  <div className="flex gap-3 items-start cursor-pointer group" onClick={onChange}>
    <div className="relative mt-0.5 shrink-0">
      <input type="checkbox" className="sr-only" checked={checked} onChange={() => {}} />
      <div className={`w-5 h-5 border-2 rounded-md transition-all duration-200 ${
        checked
          ? "border-emerald-500 bg-emerald-500"
          : "border-gray-300 bg-white group-hover:border-emerald-300"
      }`}>
        <svg className={`w-3 h-3 text-white m-0.5 transition-opacity ${checked ? "opacity-100" : "opacity-0"}`}
          fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd"
            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
            clipRule="evenodd" />
        </svg>
      </div>
    </div>
    <span className={`text-sm leading-relaxed transition-colors ${
      checked ? "text-emerald-700 font-medium" : "text-gray-700 group-hover:text-emerald-600"
    }`}>
      {children}
    </span>
  </div>
);

/* ─── main component ─────────────────────────────────────────── */
const NewClaimForm = () => {
  const navigate      = useNavigate();
  const location      = useLocation();
  const policy        = location.state?.policy;
  const forSelf       = location.state?.forSelf !== false;
  const representative = location.state?.representative || null;

  const [step, setStep] = useState(0);

  /* ── incident state + validation ── */
  const [incident, setIncident] = useState({
    date: "", location: "", type: "Accident", authorities: "no", description: "",
  });
  const [incidentErrors, setIncidentErrors] = useState({});

  const incidentRefs = {
    date:        useRef(null),
    location:    useRef(null),
    description: useRef(null),
  };

  const validateIncident = (inc) => {
    const e = {};
    if (!inc.date)                    e.date        = "Date of incident is required";
    if (!inc.location.trim())         e.location    = "Location is required";
    if (!inc.description.trim())      e.description = "Please describe the incident";
    if (inc.description.length > 255) e.description = "Description must be 255 characters or fewer";
    return e;
  };

  const scrollToFirstIncidentError = (errs) => {
    for (const key of ["date", "location", "description"]) {
      if (errs[key] && incidentRefs[key]?.current) {
        incidentRefs[key].current.scrollIntoView({ behavior: "smooth", block: "center" });
        incidentRefs[key].current.querySelector("input,textarea")?.focus({ preventScroll: true });
        break;
      }
    }
  };

  /* ── documents state ── */
  const [uploads, setUploads] = useState([]);

  /* ── declarations state ── */
  const [declarations, setDeclarations] = useState({ accuracy: false, authorization: false });

  /* ── submit state ── */
  const [submitted,    setSubmitted]    = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError,  setSubmitError]  = useState(null);

  /* ── derived ── */
  const steps = forSelf ? SELF_CLAIM_STEPS : REPRESENTATIVE_CLAIM_STEPS;
  const currentSidebarIndex = forSelf ? step : step + 1;

  /* ── step navigation ── */
  const handleStepClick = (sidebarIdx) => {
    // For representative claims, sidebar index 0 = "Your details" page
    if (!forSelf && sidebarIdx === 0) {
      navigate("/claim/new/relative", { state: { policy, representative } });
      return;
    }
    // Map sidebar index → form step
    const formStep = forSelf ? sidebarIdx : sidebarIdx - 1;
    // Only allow navigating to reached or completed steps
    if (formStep >= 0 && formStep <= step) {
      setStep(formStep);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const canSubmit =
    incident.date && incident.location.trim() &&
    incident.description.trim() && incident.description.length <= 255 &&
    incident.authorities && declarations.accuracy && declarations.authorization;

  /* ── file helpers ── */
  const handleFiles = (files) => {
    const valid = Array.from(files).filter((f) => f.size <= 2 * 1024 * 1024);
    setUploads((prev) => [...prev, ...valid].slice(0, 4));
  };
  const removeFile = (i) => setUploads((prev) => prev.filter((_, j) => j !== i));

  const getFileIcon = (file) => {
    const t = file.type.toLowerCase();
    if (t.includes("pdf"))   return "📄";
    if (t.includes("word") || t.includes("document")) return "📝";
    if (t.includes("image")) return "🖼️";
    return "📎";
  };
  const fmtSize = (b) => {
    if (!b) return "0 B";
    const k = 1024, i = Math.floor(Math.log(b) / Math.log(k));
    return `${(b / Math.pow(k, i)).toFixed(1)} ${["B","KB","MB"][i]}`;
  };

  /* ── submit ── */
  const fileToBase64 = (file) =>
    new Promise((res, rej) => {
      const r = new FileReader();
      r.readAsDataURL(file);
      r.onload  = () => res(r.result);
      r.onerror = rej;
    });

  const submit = async () => {
    if (!canSubmit) return;
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const supportingDocuments = await Promise.all(uploads.map(fileToBase64));
      const policySlug = getClaimPolicySlug(policy);
      const insuranceProviderSlug =
        (typeof policy?.insurance_provider_slug === "string" && policy.insurance_provider_slug) ||
        policy?.insurance_policy?.insurance_provider_slug ||
        (typeof policy?.insurance_policy?.insurance_provider === "object"
          ? policy.insurance_policy.insurance_provider.slug
          : policy?.insurance_policy?.insurance_provider) || null;

      if (!policySlug) {
        setSubmitError("Policy slug is missing. Please start the claim again.");
        return;
      }
      if (!insuranceProviderSlug || typeof insuranceProviderSlug !== "string") {
        setSubmitError("Insurance provider information is missing. Please try again or contact support.");
        return;
      }
      const payload = {
        incident: [
          {
            key: "incident",
            date: incident.date,
            location: incident.location,
            type: incident.type,
            authorities: incident.authorities,
            description: incident.description,
          },
          {
            ...(policy?.purchased_policy_data || {}),
            key: "purchased_policy",
          },
          {
            ...(policy?.customer || {}),
            key: "customer",
          },
        ],
        documents: supportingDocuments,
        declarations: [
          declarations.accuracy      && "I declare the above information is accurate and true to the best of my knowledge",
          declarations.authorization && "I authorize tripsecure and insurance provider to contact me regarding this claim",
        ].filter(Boolean),
        policy_slug:              policySlug,
        insurance_provider_slug:  insuranceProviderSlug,
        for_self:                 forSelf,
        ...(!forSelf && representative ? { representative } : {}),
      };
      console.log("Claim payload:", JSON.stringify(shortenPayloadForLog(payload), null, 2));
      const result = await claimService.createClaim(payload);
      if (result.status && result?.data?.status_code === 200) setSubmitted(true);
      else setSubmitError(result.message || "Failed to submit claim. Please try again.");
    } catch {
      setSubmitError("An unexpected error occurred. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  /* ── success screen ─────────────────────────────────────────── */
  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-green-50">
        <div className="pt-24 lg:pt-28" />
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-md">
            <div className="overflow-hidden relative bg-white rounded-3xl border border-gray-100 shadow-2xl">
              <div className="absolute inset-0 bg-gradient-to-br via-transparent from-emerald-500/5 to-green-500/5" />
              <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full blur-3xl bg-emerald-500/10" />
              <div className="relative p-8 text-center">
                <div className="relative mx-auto mb-6 w-20 h-20">
                  <div className="flex justify-center items-center w-20 h-20 bg-gradient-to-br from-emerald-500 to-green-600 rounded-full shadow-lg">
                    <CheckCircle2 size={36} className="text-white" />
                  </div>
                  <div className="absolute inset-0 w-20 h-20 rounded-full animate-ping bg-emerald-500/30" />
                </div>
                <h2 className="mb-2 text-2xl font-bold text-gray-900">Claim Submitted! 🎉</h2>
                <p className="leading-relaxed text-gray-600">
                  Your claim has been received and is being processed. We'll keep you updated on its progress.
                </p>
                {policy && (
                  <div className="p-4 my-6 bg-gray-50 rounded-2xl border border-gray-100">
                    <p className="mb-1 text-xs font-semibold tracking-wide text-gray-400 uppercase">Claim Reference</p>
                    <p className="font-mono text-sm font-bold text-gray-900">
                      #{policy.number}-CLM-{Date.now().toString().slice(-6)}
                    </p>
                  </div>
                )}
                <button onClick={() => navigate("/claim/list")}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-emerald-600 to-green-700 px-6 py-3.5 text-sm font-semibold text-white shadow-lg hover:shadow-xl transition-all">
                  Track Your Claims
                </button>
                <p className="mt-6 text-xs text-gray-400">
                  Need help?{" "}
                  <span className="font-semibold text-emerald-600">support@tripsecure.com</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* ── step meta ── */
  const stepMeta = [
    { icon: ClipboardList, title: "Incident details",     subtitle: "Tell us what happened" },
    { icon: Upload,        title: "Supporting documents", subtitle: "Attach any evidence or reports (optional)" },
    { icon: FileText,      title: "Declaration",          subtitle: "Confirm the accuracy of your submission" },
  ];
  const { icon: StepIcon, title: stepTitle, subtitle: stepSubtitle } = stepMeta[step];

  /* ── render ─────────────────────────────────────────────────── */
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
      <div className="pt-20" />

      {/* mobile stepper */}
      <div className="lg:hidden px-4 pt-4 sm:px-6">
        <ClaimStepper steps={steps} currentIndex={currentSidebarIndex} onStepClick={handleStepClick} />
      </div>

      <div className="mx-auto max-w-7xl px-4 pb-20 sm:px-6 lg:px-8">
        <div className="flex gap-10 items-start pt-8">
          {/* sidebar */}
          <SidebarStepper
            steps={steps}
            currentIndex={currentSidebarIndex}
            policy={policy}
            forSelf={forSelf}
            representative={representative}
            onStepClick={handleStepClick}
          />

          {/* main content */}
          <div className="flex-1 min-w-0">
            {/* page header */}
            <div className="flex gap-4 justify-between items-start mb-6">
              <div className="flex flex-1 gap-4 items-start min-w-0">
                <div className="grid place-items-center w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-500 to-green-600 text-white shadow-lg shadow-emerald-200 shrink-0">
                  <Shield size={22} />
                </div>
                <div className="min-w-0">
                  <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl leading-tight">
                    {forSelf ? "Submit a claim" : "Claim on behalf of policyholder"}
                  </h1>
                  <p className="mt-1 text-sm text-gray-500">
                    {forSelf
                      ? "Complete each step carefully — your information helps us process your claim faster."
                      : `Submitting for ${representative?.full_name || "the policyholder"} · step ${currentSidebarIndex + 1} of ${steps.length}`}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="group inline-flex items-center gap-2 h-10 sm:h-11 shrink-0 rounded-full border border-gray-200 bg-white pl-1.5 pr-4 text-sm font-semibold text-gray-700 shadow-sm hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-800 hover:shadow-md transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 focus-visible:ring-offset-2"
              >
                <span className="grid place-items-center w-7 h-7 rounded-full bg-gray-100 text-gray-600 transition-colors group-hover:bg-emerald-100 group-hover:text-emerald-700">
                  <ChevronLeft size={16} aria-hidden="true" />
                </span>
                Back
              </button>
            </div>

            {/* steps */}
            <AnimatePresence mode="wait">

              {/* ── Step 0: Incident ──────────────────────────────── */}
              {step === 0 && (
                <motion.div key="step-0"
                  variants={stepVariants} initial="enter" animate="center" exit="exit"
                  transition={{ duration: 0.25, ease: "easeInOut" }}
                  className="space-y-6">

                  <section className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                    <div className="px-6 pt-5 pb-3 border-b border-gray-50">
                      <div className="flex gap-2 items-center">
                        <ClipboardList size={16} className="text-emerald-600" />
                        <h2 className="text-sm font-bold text-gray-900 uppercase tracking-wide">Incident details</h2>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Tell us when and where the incident happened</p>
                    </div>

                    <div className="grid gap-5 sm:grid-cols-2 px-6 py-5">
                      {/* date */}
                      <div ref={incidentRefs.date}>
                        <label className={labelCls}>Date of incident <span className="text-red-500">*</span></label>
                        <input type="date"
                          className={incidentErrors.date ? inputErr : inputNormal}
                          value={incident.date}
                          onChange={(e) => {
                            setIncident((p) => ({ ...p, date: e.target.value }));
                            if (incidentErrors.date) setIncidentErrors((p) => ({ ...p, date: "" }));
                          }} />
                        <FieldError msg={incidentErrors.date} />
                      </div>

                      {/* location */}
                      <div ref={incidentRefs.location}>
                        <label className={labelCls}>Location of incident <span className="text-red-500">*</span></label>
                        <input
                          className={incidentErrors.location ? inputErr : inputNormal}
                          placeholder="e.g. Kotoka Airport, Accra"
                          value={incident.location}
                          onChange={(e) => {
                            setIncident((p) => ({ ...p, location: e.target.value }));
                            if (incidentErrors.location) setIncidentErrors((p) => ({ ...p, location: "" }));
                          }} />
                        <FieldError msg={incidentErrors.location} />
                      </div>

                      {/* type */}
                      <div>
                        <label className={labelCls}>Type of claim</label>
                        <div className="relative">
                          <select
                            className={`${inputNormal} appearance-none pr-9`}
                            value={incident.type}
                            onChange={(e) => setIncident((p) => ({ ...p, type: e.target.value }))}>
                            {["Accident", "Medical", "Theft", "Other"].map((o) => (
                              <option key={o} value={o}>{o}</option>
                            ))}
                          </select>
                          <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                        </div>
                      </div>

                      {/* authorities */}
                      <div>
                        <label className={labelCls}>Were authorities involved?</label>
                        <div className="flex gap-4 mt-1">
                          {[{ v: "no", l: "No" }, { v: "yes", l: "Yes" }].map(({ v, l }) => (
                            <label key={v} className="flex gap-2 items-center cursor-pointer group">
                              <div className="relative">
                                <input type="radio" className="sr-only" checked={incident.authorities === v}
                                  onChange={() => setIncident((p) => ({ ...p, authorities: v }))} />
                                <div className={`w-5 h-5 rounded-full border-2 transition-all ${
                                  incident.authorities === v
                                    ? "border-emerald-500 bg-emerald-50"
                                    : "border-gray-300 group-hover:border-emerald-300"
                                }`}>
                                  {incident.authorities === v && (
                                    <div className="w-2.5 h-2.5 bg-emerald-500 rounded-full absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
                                  )}
                                </div>
                              </div>
                              <span className={`text-sm ${incident.authorities === v ? "text-emerald-700 font-semibold" : "text-gray-600"}`}>{l}</span>
                            </label>
                          ))}
                        </div>
                      </div>

                      {/* description */}
                      <div className="sm:col-span-2" ref={incidentRefs.description}>
                        <label className={labelCls}>
                          Brief description <span className="text-red-500">*</span>
                          <span className="ml-1 font-normal text-gray-400">(max 255 characters)</span>
                        </label>
                        <textarea
                          rows={4}
                          maxLength={255}
                          placeholder="Describe what happened in detail"
                          className={`w-full px-3.5 py-3 text-sm bg-white rounded-xl border transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-300 resize-none ${
                            incidentErrors.description ? "border-red-400 bg-red-50 focus:ring-red-200" : "border-gray-200"
                          }`}
                          value={incident.description}
                          onChange={(e) => {
                            setIncident((p) => ({ ...p, description: e.target.value }));
                            if (incidentErrors.description) setIncidentErrors((p) => ({ ...p, description: "" }));
                          }} />
                        <div className="flex justify-between mt-1">
                          <FieldError msg={incidentErrors.description} />
                          <p className={`text-xs ml-auto ${incident.description.length > 230 ? "text-orange-500" : "text-gray-400"}`}>
                            {incident.description.length}/255
                          </p>
                        </div>
                      </div>
                    </div>
                  </section>

                  {/* footer */}
                  <div className="flex items-center justify-end gap-3 pt-2">
                    {!forSelf && (
                      <button
                        type="button"
                        onClick={() => navigate("/claim/new/relative", { state: { policy, representative } })}
                        className="group inline-flex items-center gap-2 h-11 rounded-full border border-gray-200 bg-white pl-1.5 pr-4 text-sm font-semibold text-gray-700 shadow-sm hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-800 hover:shadow-md transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 focus-visible:ring-offset-2"
                      >
                        <span className="grid place-items-center w-7 h-7 rounded-full bg-gray-100 text-gray-600 transition-colors group-hover:bg-emerald-100 group-hover:text-emerald-700">
                          <ChevronLeft size={16} aria-hidden="true" />
                        </span>
                        Back
                      </button>
                    )}
                    <button type="button"
                      onClick={() => {
                        const errs = validateIncident(incident);
                        setIncidentErrors(errs);
                        if (Object.keys(errs).length > 0) { scrollToFirstIncidentError(errs); return; }
                        setStep(1);
                      }}
                      className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-emerald-600 to-green-700 px-6 py-3 text-sm font-bold text-white shadow-md shadow-emerald-200 hover:from-emerald-700 hover:to-green-800 transition-all">
                      Continue to documents →
                    </button>
                  </div>
                </motion.div>
              )}

              {/* ── Step 1: Documents ─────────────────────────────── */}
              {step === 1 && (
                <motion.div key="step-1"
                  variants={stepVariants} initial="enter" animate="center" exit="exit"
                  transition={{ duration: 0.25, ease: "easeInOut" }}
                  className="space-y-6">

                  <section className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                    <div className="px-6 pt-5 pb-3 border-b border-gray-50">
                      <div className="flex gap-2 items-center">
                        <Upload size={16} className="text-emerald-600" />
                        <h2 className="text-sm font-bold text-gray-900 uppercase tracking-wide">Supporting documents</h2>
                        <span className="ml-auto text-[10px] font-semibold text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">Optional</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Upload hospital reports, police reports, photos, or any other evidence (up to 4 files, 2 MB each)
                      </p>
                    </div>

                    <div className="grid gap-6 lg:grid-cols-2 px-6 py-5">
                      {/* upload zone */}
                      <div>
                        {uploads.length < 4 ? (
                          <label className="block cursor-pointer group">
                            <p className={labelCls}>Upload files</p>
                            <div className="mt-0.5 flex flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed border-gray-200 bg-gray-50 p-8 transition-colors group-hover:border-emerald-400 group-hover:bg-emerald-50/30 h-52">
                              <Upload size={28} className="text-gray-300 group-hover:text-emerald-500 transition-colors" />
                              <div className="text-center">
                                <p className="text-sm font-semibold text-gray-600 group-hover:text-emerald-700 transition-colors">Click to upload</p>
                                <p className="text-xs text-gray-400 mt-0.5">PNG · JPG · PDF · DOC · DOCX</p>
                                <p className="text-xs text-gray-400">Max {4 - uploads.length} more · 2 MB each</p>
                              </div>
                            </div>
                            <input type="file" multiple className="sr-only"
                              accept="image/*,application/pdf,.doc,.docx"
                              onChange={(e) => handleFiles(e.target.files)} />
                          </label>
                        ) : (
                          <div className="flex flex-col items-center justify-center gap-3 h-52 rounded-xl border-2 border-emerald-200 bg-emerald-50">
                            <CheckCircle2 size={28} className="text-emerald-500" />
                            <div className="text-center">
                              <p className="text-sm font-semibold text-emerald-700">Maximum files reached</p>
                              <p className="text-xs text-emerald-600">Remove a file to upload more</p>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* file list */}
                      <div>
                        <p className={labelCls}>
                          {uploads.length > 0 ? `Uploaded files (${uploads.length}/4)` : "File previews"}
                        </p>
                        {uploads.length > 0 ? (
                          <div className="mt-0.5 space-y-2 max-h-52 overflow-y-auto pr-1">
                            {uploads.map((file, i) => (
                              <div key={i} className="flex gap-3 items-center p-3 bg-gray-50 rounded-xl border border-gray-100">
                                <div className="grid place-items-center w-9 h-9 bg-white rounded-lg border border-gray-200 text-base shrink-0">
                                  {getFileIcon(file)}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-xs font-semibold text-gray-800 truncate">{file.name}</p>
                                  <p className="text-[10px] text-gray-400">{fmtSize(file.size)}</p>
                                </div>
                                <button type="button" onClick={() => removeFile(i)}
                                  className="grid place-items-center w-7 h-7 text-red-500 bg-red-50 rounded-lg hover:bg-red-100 shrink-0">
                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                </button>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="mt-0.5 flex flex-col items-center justify-center h-52 rounded-xl border border-gray-100 bg-gray-50">
                            <FileText size={28} className="text-gray-200" />
                            <p className="mt-2 text-xs text-gray-400">No files uploaded yet</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </section>

                  <div className="flex justify-end items-center gap-3 pt-2">
                    <button
                      type="button"
                      onClick={() => setStep(0)}
                      className="group inline-flex items-center gap-2 h-11 rounded-full border border-gray-200 bg-white pl-1.5 pr-4 text-sm font-semibold text-gray-700 shadow-sm hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-800 hover:shadow-md transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 focus-visible:ring-offset-2"
                    >
                      <span className="grid place-items-center w-7 h-7 rounded-full bg-gray-100 text-gray-600 transition-colors group-hover:bg-emerald-100 group-hover:text-emerald-700">
                        <ChevronLeft size={16} aria-hidden="true" />
                      </span>
                      Back
                    </button>
                    <button type="button" onClick={() => setStep(2)}
                      className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-emerald-600 to-green-700 px-6 py-3 text-sm font-bold text-white shadow-md shadow-emerald-200 hover:from-emerald-700 hover:to-green-800 transition-all">
                      Continue to declaration →
                    </button>
                  </div>
                </motion.div>
              )}

              {/* ── Step 2: Declaration ───────────────────────────── */}
              {step === 2 && (
                <motion.div key="step-2"
                  variants={stepVariants} initial="enter" animate="center" exit="exit"
                  transition={{ duration: 0.25, ease: "easeInOut" }}
                  className="space-y-6">

                  <section className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                    <div className="px-6 pt-5 pb-3 border-b border-gray-50">
                      <div className="flex gap-2 items-center">
                        <FileText size={16} className="text-emerald-600" />
                        <h2 className="text-sm font-bold text-gray-900 uppercase tracking-wide">Declaration</h2>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Please read and confirm the statements below before submitting your claim
                      </p>
                    </div>

                    <div className="px-6 py-5 space-y-4">
                      <Checkbox
                        checked={declarations.accuracy}
                        onChange={() => setDeclarations((p) => ({ ...p, accuracy: !p.accuracy }))}>
                        I declare that the information provided above is accurate and true to the best of my knowledge
                      </Checkbox>
                      <Checkbox
                        checked={declarations.authorization}
                        onChange={() => setDeclarations((p) => ({ ...p, authorization: !p.authorization }))}>
                        I authorize TripSecure and the insurance provider to contact me regarding this claim
                      </Checkbox>

                      {/* incomplete warning */}
                      <AnimatePresence>
                        {(!declarations.accuracy || !declarations.authorization) && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                            className="flex gap-2 items-start rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
                            <AlertCircle size={14} className="text-amber-500 mt-0.5 shrink-0" />
                            <p className="text-xs text-amber-700">
                              Both declarations must be accepted before you can submit your claim.
                            </p>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>

                    {/* incident summary */}
                    <div className="mx-6 mb-5 rounded-xl border border-gray-100 bg-gray-50 p-4">
                      <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-3">Claim summary</p>
                      <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                        <div>
                          <dt className="text-gray-400 font-medium">Date</dt>
                          <dd className="text-gray-800 font-semibold mt-0.5">{incident.date || "—"}</dd>
                        </div>
                        <div>
                          <dt className="text-gray-400 font-medium">Type</dt>
                          <dd className="text-gray-800 font-semibold mt-0.5">{incident.type}</dd>
                        </div>
                        <div className="col-span-2">
                          <dt className="text-gray-400 font-medium">Location</dt>
                          <dd className="text-gray-800 font-semibold mt-0.5">{incident.location || "—"}</dd>
                        </div>
                        <div className="col-span-2">
                          <dt className="text-gray-400 font-medium">Documents attached</dt>
                          <dd className="text-gray-800 font-semibold mt-0.5">{uploads.length} file{uploads.length !== 1 ? "s" : ""}</dd>
                        </div>
                      </dl>
                    </div>
                  </section>

                  <div className="flex justify-end items-center gap-3 pt-2">
                    <button
                      type="button"
                      onClick={() => setStep(1)}
                      className="group inline-flex items-center gap-2 h-11 rounded-full border border-gray-200 bg-white pl-1.5 pr-4 text-sm font-semibold text-gray-700 shadow-sm hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-800 hover:shadow-md transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 focus-visible:ring-offset-2"
                    >
                      <span className="grid place-items-center w-7 h-7 rounded-full bg-gray-100 text-gray-600 transition-colors group-hover:bg-emerald-100 group-hover:text-emerald-700">
                        <ChevronLeft size={16} aria-hidden="true" />
                      </span>
                      Back
                    </button>

                    <div className="flex flex-col items-end gap-2">
                      <AnimatePresence>
                        {submitError && (
                          <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
                            className="flex gap-2 items-center px-4 py-2.5 rounded-xl border border-red-200 bg-red-50 text-xs text-red-700 max-w-xs text-right">
                            <AlertCircle size={13} className="shrink-0" /> {submitError}
                          </motion.div>
                        )}
                      </AnimatePresence>

                      <button type="button"
                        disabled={!canSubmit || isSubmitting}
                        onClick={submit}
                        className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-emerald-600 to-green-700 px-6 py-3 text-sm font-bold text-white shadow-md shadow-emerald-200 hover:from-emerald-700 hover:to-green-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all">
                        {isSubmitting ? (
                          <><Loader2 size={16} className="animate-spin" /> Submitting…</>
                        ) : (
                          "Submit claim"
                        )}
                      </button>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewClaimForm;
